import { useState, useEffect, Suspense } from "react";
import { toast } from "sonner";
import { Navigate, Outlet, useNavigate, useLocation, useNavigationType, Link } from "react-router-dom";
import { Sun, ListChecks, Menu, ClipboardCheck, Package, Bell, Search, MapPin, LogOut } from "lucide-react";
import { useAuth, useGet } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/fmt";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { NetStatus, Loading } from "./Bits";
import QuickAdd from "./QuickAdd";
import SidebarNav from "./SidebarNav";
import WorkspaceBar from "./WorkspaceBar";
import { SECTIONS, WorkspaceCtx, visibleSections, allLeaves, resolveNav } from "./navConfig";
import AskDrawer from "./AskDrawer";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { ReauthDialog } from "@/components/security/Mfa";
import { ErrorBoundary } from "./AppStates";
import UpgradeGate from "./UpgradeGate";
import { useLiveUpdates } from "@/lib/liveUpdates";
import { VerifyBanner } from "@/pages/public/VerifyEmail";

const BOTTOM = [["today", "Today", Sun], ["shift-ops", "Work", ListChecks], ["close", "Close", ClipboardCheck], ["inventory", "Inventory", Package]];
const WORK_KEY = "86d_work_last";

function BottomNav({ sections, ws, path }) {
  const byId = Object.fromEntries(sections.map((s) => [s.id, s]));
  const last = localStorage.getItem(WORK_KEY);
  const tabs = BOTTOM.filter(([id]) => byId[id]).map(([id, label, icon]) => {
    const s = byId[id];
    const to = id === "shift-ops" && allLeaves([s]).some((l) => l.to === last && !l.locked) ? last : s.to;
    return { id, label, icon, to, on: ws?.section.id === id };
  });
  tabs.push({ id: "more", label: "More", icon: Menu, to: "/app/more", on: path === "/app/more" || (!!ws && !tabs.some((t) => t.id === ws.section.id)) });
  return (
    <nav className="no-print lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#1C1917] text-stone-400 border-t border-stone-800 pb-[env(safe-area-inset-bottom)]" aria-label="Main" data-testid="bottom-nav">
      <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((t) => (
          <Link key={t.id} to={t.to} aria-current={t.on ? "page" : undefined} aria-label={t.label} data-testid={`tab-${t.label.toLowerCase()}`}
            className={`relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#e8b4a0] ${t.on ? "text-white" : ""}`}>
            {t.on && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-[#e8b4a0]" />}
            <t.icon className="h-5 w-5" /> {t.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

export function LocationSelect({ className = "" }) {
  const { locations, locationId, switchLocation } = useAuth();
  return (
    <label className={`flex items-center gap-2 rounded-full bg-stone-900 text-white pl-3 pr-2 h-10 ${className}`} data-testid="location-selector">
      <MapPin className="h-4 w-4 text-[#e8b4a0] shrink-0" />
      <select data-testid="location-select" value={locationId || ""} onChange={(e) => switchLocation(e.target.value)} disabled={locations.length < 2}
        className="bg-transparent text-sm font-semibold outline-none max-w-[120px] sm:max-w-[190px] truncate appearance-none pr-1 disabled:opacity-100">
        {locations.map((l) => <option key={l.id} value={l.id} className="text-stone-900">{l.name}</option>)}
      </select>
    </label>
  );
}

export function InboxBadge({ className = "" }) {
  const { has } = useAuth();
  const { data } = useGet("/inbox/unread-count", { enabled: has("messages_inbox"), refetchInterval: 20000 });
  if (!data?.count) return null;
  return <span className={`ml-auto min-w-[20px] h-5 rounded-full bg-[#9E2A2B] text-white text-[11px] font-bold flex items-center justify-center px-1.5 ${className}`} aria-label={`${data.count} conversations with unread messages`} data-testid="inbox-unread-badge">{data.count}</span>;
}

function Notifications() {
  const { data, refetch } = useGet("/notifications", { refetchInterval: 60000 });
  const nav = useNavigate();
  return (
    <Popover onOpenChange={(o) => !o && data?.unread && api.post("/notifications/read-all").then(refetch)}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-btn" className="relative h-10 w-10 rounded-full hover:bg-[#F3EFE6] flex items-center justify-center" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {!!data?.unread && <span data-testid="notifications-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#9E2A2B] text-white text-[10px] font-bold flex items-center justify-center px-1">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b font-medium">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {!data?.items?.length && <div className="p-6 text-sm text-stone-500 text-center">You're all caught up.</div>}
          {data?.items?.map((n) => (
            <button key={n.id} onClick={() => nav(n.link.split("#")[0])} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-[#FBF9F5] ${n.read ? "" : "bg-amber-50/40"}`}>
              <div className="text-xs uppercase tracking-wider text-stone-500">{n.category === "critical" ? "⚠ Critical" : n.category}</div>
              <div className="text-sm font-medium">{n.title}</div>
              {n.body && <div className="text-xs text-stone-500 line-clamp-2">{n.body}</div>}
              <div className="text-[11px] text-stone-400 mt-1">{timeAgo(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SearchDialog() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const auth = useAuth();
  const { locationId, switchLocation } = auth;
  const pages = q.trim() ? allLeaves(visibleSections(auth)).filter((l) => l.search.includes(q.trim().toLowerCase())).slice(0, 6) : [];
  const { data, isFetching } = useGet(q.length >= 2 ? `/search?q=${encodeURIComponent(q)}` : null);
  return (
    <>
      <button data-testid="search-btn" onClick={() => setOpen(true)} className="h-10 w-10 md:w-56 rounded-full md:border md:border-[#E6E1D7] md:bg-white flex items-center justify-center md:justify-start md:px-3 gap-2 text-stone-500 text-sm hover:bg-[#F3EFE6]" aria-label="Search">
        <Search className="h-5 w-5 md:h-4 md:w-4" /> <span className="hidden md:inline">Search everything…</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl top-[20%] translate-y-0" data-testid="search-dialog">
          <DialogTitle className="sr-only">Search</DialogTitle>
          <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people, shifts, products, invoices, logs…" className="w-full h-12 text-lg border-b outline-none" />
          <div className="max-h-[50vh] overflow-y-auto -mx-2">
            {!!pages.length && <div data-testid="search-pages"><div className="text-[11px] uppercase tracking-wider text-stone-500 px-2 pt-2">Pages</div>{pages.map((l) => (
              <button key={l.id} data-testid={`search-page-${l.id}`} onClick={() => { setOpen(false); setQ(""); nav(l.to); }} className="w-full text-left p-2 rounded-lg hover:bg-[#FBF9F5]">
                <div className="text-sm font-medium">{l.label}</div><div className="text-xs text-stone-500">{l.path}</div>
              </button>))}</div>}
            {isFetching && <div className="text-sm text-stone-500 p-2">Searching…</div>}
            {data?.results?.length === 0 && <div className="text-sm text-stone-500 p-2">No matches.</div>}
            {data?.results?.map((r) => (
              <button key={r.type + r.id} data-testid="search-result" data-type={r.type} onClick={() => { setOpen(false); if (r.location_id && r.location_id !== locationId) switchLocation(r.location_id); nav(r.link); }} className="w-full text-left p-2 rounded-lg hover:bg-[#FBF9F5]">
                <div className="text-[11px] uppercase tracking-wider text-stone-500">{r.type}{r.date ? ` · ${r.date}` : ""}{r.location ? ` · ${r.location}` : ""}</div>
                <div className="text-sm font-medium">{r.title}</div>
                {r.snippet && <div className="text-xs text-stone-500 line-clamp-1">{r.snippet}</div>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Banner() {
  const { state, can, org } = useAuth();
  if (org?.is_test_tenant) return <div data-testid="test-tenant-banner" className="no-print px-4 py-2 text-sm bg-sky-50 text-sky-900"><b>Integration test restaurant.</b> Test data only. Excluded from billing, live reporting, marketing and notifications.</div>;
  if (!state) return null;
  const s = state.status;
  const map = {
    trialing: state.trial_days_left <= 5 && { tone: "bg-[#F3EFE6] text-stone-800", text: `${state.trial_days_left} days left in your free trial.` },
    trial_expired: { tone: "bg-rose-50 text-rose-900", text: "Your free trial has ended. Your data is safe — choose a plan to keep adding records." },
    canceled: { tone: "bg-rose-50 text-rose-900", text: "Your subscription is cancelled. Choose a plan to reactivate." },
    past_due: { tone: "bg-amber-50 text-amber-900", text: `Your last payment failed. Everything keeps working${state.grace_days_left != null ? ` for ${state.grace_days_left} more day${state.grace_days_left === 1 ? "" : "s"}` : ""} — update your payment method to avoid read-only mode.` },
    past_due_locked: { tone: "bg-rose-50 text-rose-900", text: "Your payment is overdue, so the account is read-only. Update your payment method to restore editing. Your data is safe." },
  };
  const b = map[s];
  if (!b) return null;
  return (
    <div data-testid="subscription-banner" className={`no-print px-4 py-2 text-sm flex items-center justify-between gap-3 ${b.tone}`}>
      <span>{b.text}</span>
      {can("billing") && <Link to="/app/billing" className="font-semibold underline whitespace-nowrap" data-testid="banner-billing-link">Choose plan</Link>}
    </div>
  );
}

function RefreshNotice() {
  const { refreshError, reload } = useAuth();
  if (!refreshError) return null;
  return (
    <div data-testid="access-refresh-error" className="no-print px-4 py-2 text-sm flex items-center justify-between gap-3 bg-amber-50 text-amber-900" role="status">
      <span>We couldn't refresh your access just now, so you're seeing your last confirmed menu.</span>
      <button type="button" onClick={reload} className="font-semibold underline whitespace-nowrap" data-testid="access-refresh-retry">Retry</button>
    </div>
  );
}

function RouteGate({ children }) {
  const { can, allowed, planFor, features, moduleFeatures } = useAuth();
  const { pathname } = useLocation();
  const item = resolveNav(SECTIONS, pathname, false)?.view;
  if (item?.module && !can(item.module) && item.module === "dashboard") return <Navigate to="/app/today" replace />;
  if (item?.module && !can(item.module) && !(item.alt && can(item.alt))) {
    const feat = moduleFeatures[item.module] || "ops.core";
    const planOk = features.includes(feat);
    if (allowed(item.module) && !planOk) return <UpgradeGate detail={`upgrade_required:${planFor(item.module)}:${feat}`} />;
    if (!planOk) return <UpgradeGate detail={`permission_and_plan_required:${planFor(item.module)}:${feat}`} />;
    return (
    <div className="card-surface p-8 text-center max-w-md mx-auto mt-10" data-testid="no-access">
      <div className="font-display text-2xl mb-2">You don't have access to {item.label}</div>
      <p className="text-sm text-stone-600 mb-4">Your account doesn't have permission for this area. Ask the owner if you need it.</p>
      <Link to="/app/today" className="underline text-sm" data-testid="no-access-home">Go to Today</Link>
    </div>);
  }
  return children;
}

export default function AppShell() {
  const auth = useAuth();
  const { can, user, org, logout, locationId, switchLocation, locations } = auth;
  useLiveUpdates();
  const nav = useNavigate();
  const navType = useNavigationType();
  const home = can("dashboard") ? "/app/dashboard" : "/app/today";
  const { pathname: path, search } = useLocation();
  const wantLoc = new URLSearchParams(search).get("loc");
  useEffect(() => {
    if (!wantLoc || wantLoc === locationId) return;
    if (locations.some((l) => l.id === wantLoc)) { switchLocation(wantLoc); toast.info(`Switched to ${locations.find((l) => l.id === wantLoc).name} to open this record`); }
    else toast.error("This record belongs to a location you can't access.");
  }, [wantLoc]); // eslint-disable-line react-hooks/exhaustive-deps
  const sections = visibleSections(auth);
  const ws = resolveNav(sections, path);
  useEffect(() => { if (navType !== "POP") window.scrollTo(0, 0); }, [path, navType]);
  useEffect(() => { if (ws?.section.id === "shift-ops" && !ws.view.locked) localStorage.setItem(WORK_KEY, ws.view.to); }, [ws?.section.id, ws?.view.to, ws?.view.locked]);
  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <aside className="no-print hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#1C1917] text-stone-300 flex-col z-30">
        <Link to={home} className="px-6 pt-6 pb-4 block">
          <div className="font-display text-3xl text-white">86'd</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mt-1 truncate" data-testid="sidebar-company">{org?.name}</div>
          {locations?.find((l) => l.id === locationId)?.name && <div className="text-xs text-stone-400 mt-0.5 truncate" data-testid="sidebar-location">{locations.find((l) => l.id === locationId).name}</div>}
        </Link>
        <nav className="flex-1 overflow-y-auto px-3 pb-6" data-testid="sidebar-nav">
          <SidebarNav sections={sections} ws={ws} Badge={InboxBadge} />
        </nav>
        <div className="border-t border-white/10 p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#9E2A2B] text-white flex items-center justify-center text-sm font-semibold">{user?.name?.[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-stone-500 truncate">{user?.title || user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="p-2 hover:text-white" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>
      <div className="lg:pl-64">
        <ImpersonationBanner />
        <ReauthDialog />
        <RefreshNotice />
        <Banner />
        <header className="no-print sticky top-0 z-20 bg-[#FBF9F5]/90 backdrop-blur-md border-b border-[#E6E1D7]">
          <div className="flex items-center gap-1.5 sm:gap-2 px-3 md:px-8 h-16">
            <Link to={home} className="lg:hidden font-display text-2xl mr-1">86'd</Link>
            <LocationSelect />
            <div className="flex-1" />
            <NetStatus />
            <AskDrawer />
            <SearchDialog />
            <Notifications />
          </div>
        </header>
        <main className="px-4 md:px-8 py-6 md:py-8 pb-safe max-w-[1400px]">
          <VerifyBanner />
          <WorkspaceCtx.Provider value={ws}>
            <WorkspaceBar ws={ws} />
            <Suspense fallback={<Loading />}><RouteGate><ErrorBoundary key={path}><Outlet /></ErrorBoundary></RouteGate></Suspense>
          </WorkspaceCtx.Provider>
          <footer className="no-print mt-12 pt-4 border-t border-[#E6E1D7] flex flex-wrap gap-x-5 gap-y-2 text-xs text-stone-600" data-testid="app-footer">
            <span>© {new Date().getFullYear()} 86operations</span>
            <Link to="/privacy" className="underline-offset-4 hover:underline hover:text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30 rounded" data-testid="app-footer-privacy">Privacy Notice</Link>
            <Link to="/security" className="underline-offset-4 hover:underline hover:text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30 rounded" data-testid="app-footer-security">Security &amp; Trust</Link>
          </footer>
        </main>
      </div>
      <BottomNav sections={sections} ws={ws} path={path} />
      {(!/^\/app\/settings\/[^/]+$/.test(path) || path.endsWith("/tips")) && !/^\/app\/close\/[^/]+$/.test(path) && path !== "/app/more" ? <QuickAdd /> : null}
    </div>
  );
}
